import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import clsx from 'clsx';
import { Helmet } from 'react-helmet';
import { useTranslation, Trans } from 'react-i18next';
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';

// CONSTANTS
import { LOCATIONS_NOT_FOUND_PAGE } from '@/constants/locations';
import { ROUTE_SEARCH, ROUTE_ROOT } from '@/constants/routes';

// UTILITY
import { analyticsPageLoaded } from '@/utility/analytics';
import { routeEnhance } from '@/utility/routes';
import { getS3ImageUrl } from '@/utility/content';

// HOC
import withRouter from '@/hoc/withRouter';

// COMPONENTS
import RouterLink from '@/components/Link';

// STYLES
import { Container } from './styles';

const NotFoundImg = getS3ImageUrl('/404/404.png');
const NotFoundImgX2 = getS3ImageUrl('/404/404-x2.png');
const NotFoundImgMob = getS3ImageUrl('/404/404-mob.png');

const NotFound = props => {
  const [t] = useTranslation();
  const [search, setSearch] = useState('');

  const {
    router: { location, navigate },
    partner,
    partnerLoading,
  } = props;

  const isPartner = !!partner.slug;

  const handleSearchChange = ({ target: { value } }) => setSearch(value);

  const handleSubmitSearch = e => {
    e.preventDefault();

    if (search.trim() !== '') {
      const enhancedRoute = routeEnhance(`${ROUTE_SEARCH}?search=${search}`);

      navigate(enhancedRoute, { replace: true, state: enhancedRoute.state });
    }
  };

  return (
    <Container className={clsx('not-found', isPartner && 'not-found--partner')}>
      {!partnerLoading && (
        <Helmet
          onChangeClientState={() =>
            analyticsPageLoaded('Page not found', {
              from_page_path: location.state?.from_page_path || '',
              from_search_params: location.state?.from_search_params || '',
            })
          }
          titleTemplate="%s | MyBundle"
        >
          <title>Page Not Found</title>
        </Helmet>
      )}
      <div className="page-content">
        <picture>
          <source srcSet={`${NotFoundImgX2} 2x`} />
          <source media="(max-width: 600px)" srcSet={NotFoundImgMob} />
          <img className="not-found__img" src={NotFoundImg} alt="Not found" />
        </picture>
        <Typography className="m-b-25" variant="h2" component="h1" align="center">
          {t('notFound.title')}
        </Typography>
        <Typography className="not-found__subtitle m-b-25" align="center">
          <Trans
            i18nKey="notFound.subtitle"
            components={{
              a: (
                <Link
                  className="text-color--purple"
                  underline="always"
                  to={ROUTE_ROOT}
                  component={RouterLink}
                  location={LOCATIONS_NOT_FOUND_PAGE}
                />
              ),
            }}
          />
        </Typography>
        <div className="not-found__search">
          <form onSubmit={handleSubmitSearch}>
            <TextField
              placeholder={t('notFound.searchPlaceholder')}
              size="small"
              value={search}
              onChange={handleSearchChange}
              InputProps={{
                endAdornment: (
                  <Button type="submit" color="primary" size="medium">
                    {t('buttons.search')}
                  </Button>
                ),
              }}
            />
          </form>
        </div>
      </div>
    </Container>
  );
};

NotFound.propTypes = {
  router: PropTypes.object.isRequired,
  partner: PropTypes.object.isRequired,
  partnerLoading: PropTypes.bool.isRequired,
};

const mapStateToProps = ({ partner }) => ({
  partner: partner.data,
  partnerLoading: partner.loadings.partner,
});

export default compose(connect(mapStateToProps), withRouter)(NotFound);
